import React from 'react'
import * as yup from 'yup'

import UserModel from './user.model'
import {
	makeOptions,
	fieldTypes,
	format_useLabel,
	makeValidator,
} from '@modules/resource/resource.module'
import { AddressSchema, getFieldPresets, getFieldPreset } from '@modules/resource/fields'
import {
	bankNames,
	counties,
	countries,
	daysOfTheWeek,
	employmentSchemes,
	genders,
	heardAboutSources,
	nationalities,
	pronouns,
	foodSafetyCertificateLevels,
} from '@server/constants'

import candidateStatuses from '@server/constants/statuses/candidate-statuses'

// TODO: refactor isTrue into version checking path on values, saving repeated values check
const isTrue = (value) => value === true || value === 'true';

const isTrueRequire = { is: true, then: s => s.required() }

const dateFormatLabel = '(dd/mm/yyyy)';

// TODO: move some of these helpers elsewhere

// Yup Tests

const sortCodeTest = {
	name: "sort-code-validation",
	message: "Invalid sort code",
	test: function (value) {
		const regex = /(^[0-9]{2}-[0-9]{2}-[0-9]{2}$|^[0-9]{6}$)/i;
		return regex.test(value);
	}
}

const alwaysTrueTest = {
	name: "need-to-agree",
	message: "You have to agree work declaration",
	test: function (value) {
		return value === true
	}
}


const minimumFiveYearsTest = {
	name: 'min-5-years',
	message: 'A minimum of 5 years of address history is required',
	test: async (values) => {
		if (!values || !values.length)
			return false;

		const yearInMilliseconds = 31536000000;

		const totalInMilliseconds = values.map(value => {
			if (!value.from || !value.to) return 0;
			const fromDate = new Date(value.from);
			const toDate = new Date(value.to);
			return toDate.getTime() - fromDate.getTime();
		}).reduce((total, n) => total + n, 0);

		const totalInYears = totalInMilliseconds / yearInMilliseconds;

		return totalInYears >= 5;
	},
};

const sufficientIdentityProvidedTest = {
	name: "sufficient-identity-provided",
	message: "At least one form of ID must be provided",
	test: function (value) {
		const IDKeys = [
			'nationalIdentityCardAvailable',
			'passportAvailable',
			'visaAvailable',
			'BRPAvailable',
			'birthCertificateAvailable',
		];
		const passValues = [true, null, '', undefined]; // null || '' pass the test to wait for a user to make a selection on all IDs before executing the actual test
		for (let key of IDKeys) {
			if (passValues.includes(this.parent[key])) {
				return true;
			}
		}

		return false;
	}
}

// Schemas

const ShiftArraySchema = (key, label = '') => ({
	key,
	label,
	type: 'fieldArray',
	children: [
		{
			key: 'start',
			type: 'time',
			label: 'from',
		},
		{
			key: 'end',
			type: 'time',
			label: 'to',
		}
	],
	defaultLength: 1,
})

const InterviewResultSchema = (key, label = '') => ({
	key,
	label,
	type: 'number',
	fieldProps: {
		component: 'InterviewResultLine' // TODO: actually create this component, integrate InterviewResults.jsx with Model
	},
	validator: false, // TODO: find out why allowing interview results to be yup.number() gets a cast error with "" as a value but payrollNo. works just fine? perhaps it's a bug with nested schemas?
})

// Validators

const agreementValidator = (message = "This field is required") => makeValidator(
	yup.boolean(),
	s => s.oneOf([true], message)
);

const identityAvailableValidator = () => makeValidator(
	yup.boolean(),
	s => s.required().test(sufficientIdentityProvidedTest)
);

////
// Candidate Model
////

const preRenderArgs_simpleArray = (data, key) => {
	if (Array.isArray(data)) {
		var longest = 1;
		for (let item of data) {
			if (item[key] && item[key].length > longest) {
				longest = item[key].length;
			}
		}
		return { longest };
	} else {
		return { longest: data[key].length };
	}
};

const CandidateModel = UserModel.extend({
	resourceKey: 'candidate',
}, [
	// System Details
	getFieldPreset('profileImage'),
	{
		key: 'status',
		label: 'Status',
		type: 'select',
		fieldProps: {
			options: candidateStatuses.map(status => ({
				label: status.label,
				value: status,
			})),
		},
		defaultValue: '',
		required: 'always',
	},
	// jobTypes
	{
		key: 'jobTypes',
		label: 'Job Types',
		type: 'resourceSelect',
		defaultValue: [],
		fieldProps: {
			resource: 'jobtype',
			isMulti: true,
		},
		toCSV: {
			// TODO: abstract this function
			valueIsArray: true,
			getPreRenderArgs: preRenderArgs_simpleArray,
			formatValue: format_useLabel,
		}
	},

	// About
	{
		key: 'jobsApplyingFor',
		label: 'Jobs applying for',
		type: 'text',
		required: 'never',
	},
	{
		key: 'dateOfBirth',
		label: 'Date of Birth ' + dateFormatLabel,
		type: 'date',
		fieldProps: {
			maxDate: new Date(),
		}
	},
	{
		key: 'gender',
		label: 'Gender',
		type: 'radioGroup',
		fieldProps: {
			options: makeOptions(genders),
		}
	},
	...getFieldPresets([
		'mobile',
		['telephone', {
			required: 'never',
		}],
	]),

	// Emergency Contact Details
	{
		key: 'emergencyContact',
		type: 'object',
		label: 'Emergency Contact',
		children: [
			{
				key: 'name',
				label: 'Emergency Contact Name',
				type: 'text',
			},
			{
				key: 'relationship',
				label: 'Emergency Contact Relationship',
				type: 'text',
			},
			...getFieldPresets([
				['telephone', {
					label: 'Emergency Contact Telephone',
					required: 'never',

				}],
				['mobile', {
					label: 'Emergency Contact Mobile',
				}],
			])

		]
	},

	// Right to Work and Pre-employment screening
	{
		key: 'immigrationPermission',
		label: 'Do you have immigration permission to work in the UK?',
		type: 'yesNo',
		validator: agreementValidator('You must have permission to work in the UK to continue'),
	},

	// National Identity Card
	{
		key: 'nationalIdentityCardAvailable',
		label: 'Do you have a National Identity Card?',
		type: 'yesNo',
		required: 'conditional',
		validator: identityAvailableValidator(),
	},
	{
		key: 'nationalIdentityCardNumber',
		label: 'National Identity Card Number',
		type: 'text',
		fieldProps: {
			show: ({ values }) => values && isTrue(values.nationalIdentityCardAvailable)
		},
		required: 'conditional',
	},
	{
		key: 'nationalIdentityCardExpiry',
		label: 'National Identity Card Expiry ' + dateFormatLabel,
		type: 'date',
		fieldProps: {
			minDate: new Date(),
			show: ({ values }) => values && isTrue(values.nationalIdentityCardAvailable)
		},
		required: 'conditional',
	},
	// Passport
	{
		key: 'passportAvailable',
		label: 'Do you have a Passport?',
		type: 'yesNo',
		required: 'conditional',
		validator: identityAvailableValidator(),
	},
	{
		key: 'passportID',
		label: 'Passport ID',
		type: 'text',
		fieldProps: {
			show: ({ values }) => values && isTrue(values.passportAvailable)
		},
		required: 'conditional',
	},
	{
		key: 'passportExpiry',
		label: 'Passport Expiry Date ' + dateFormatLabel,
		type: 'date',
		fieldProps: {
			minDate: new Date(),
			show: ({ values }) => values && isTrue(values.passportAvailable)
		},
		required: 'conditional',
	},
	// Visa
	{
		key: 'visaAvailable',
		label: 'Do you have an Entry Visa?',
		type: 'yesNo',
		required: 'conditional',
		validator: identityAvailableValidator(),
	},
	{
		key: 'visaID',
		label: 'Visa ID',
		type: 'text',
		fieldProps: {
			show: ({ values }) => values && isTrue(values.visaAvailable)
		},
		required: 'conditional',
	},
	{
		key: 'visaExpiry',
		label: 'Visa Expiry Date ' + dateFormatLabel,
		type: 'date',
		fieldProps: {
			minDate: new Date(),
			show: ({ values }) => values && isTrue(values.visaAvailable)
		},
		required: 'conditional',
	},
	// BRP
	{
		key: 'BRPAvailable',
		label: 'Do you have a Biometric Residence Permit (BRP)?',
		type: 'yesNo',
		required: 'conditional',
		validator: identityAvailableValidator(),
	},
	{
		key: 'BRPID',
		label: 'BRP ID',
		type: 'text',
		fieldProps: {
			show: ({ values }) => values && isTrue(values.BRPAvailable)
		},
		required: 'conditional',
	},
	{
		key: 'BRPExpiry',
		label: 'BRP Expiry Date ' + dateFormatLabel,
		type: 'date',
		fieldProps: {
			minDate: new Date(),
			show: ({ values }) => values && isTrue(values.BRPAvailable)
		},
		required: 'conditional',
	},
	// Birth Certificate
	{
		key: 'birthCertificateAvailable',
		label: 'Do you have a British Birth Certificate?',
		type: 'yesNo',
		required: 'conditional',
		validator: identityAvailableValidator(),
	},
	{
		key: 'birthCertificateID',
		label: 'UK Birth Certificate ID',
		type: 'text',
		fieldProps: {
			show: ({ values }) => values && isTrue(values.birthCertificateAvailable)
		},
		required: 'conditional',
	},
	{
		key: 'nationality',
		label: 'Nationality',
		type: 'select',
		fieldProps: {
			options: makeOptions(nationalities),
		}
	},
	{
		key: 'countryOfBirth',
		label: "Country of Birth",
		type: 'select',
		fieldProps: {
			options: makeOptions(countries),
		}
	},
	{
		key: 'townCityOfBirth',
		label: 'Town/City of Birth',
		type: 'text',
	},
	{
		key: 'regionOfBirth',
		label: 'Region of Birth',
		type: 'text',
	},
	{
		key: 'mothersMaidenName',
		label: 'Mother\'s Maiden Name',
		type: 'text',
	},
	{
		key: 'nationalityChangedSinceBirth',
		label: 'Nationality changed since birth?',
		type: 'yesNo',
	},
	{
		key: 'birthNationality',
		label: 'Previous nationality',
		type: 'select',
		fieldProps: {
			options: makeOptions(nationalities),
			show: ({ values }) => values && isTrue(values.nationalityChangedSinceBirth),
		},
		required: 'conditional',
		validator: yup.string().when('nationalityChangedSinceBirth', isTrueRequire),
	},
	{
		key: 'dateNationalityChanged',
		label: 'Date Nationality Changed ' + dateFormatLabel,
		type: 'date',
		fieldProps: {
			maxDate: new Date(),
			show: ({ values }) => values && isTrue(values.nationalityChangedSinceBirth)
		},
		required: 'conditional',
		validator: fieldTypes.date.validator.when('nationalityChangedSinceBirth', isTrueRequire),
	},
	{
		key: 'surnameChangedSinceBirth',
		label: 'Surname changed since birth?',
		type: 'yesNo',
	},
	{
		key: 'previousSurname',
		label: 'Previous Surname',
		type: 'text',
		fieldProps: {
			show: ({ values }) => values && isTrue(values.surnameChangedSinceBirth)
		},
		required: 'conditional',
		validator: yup.string().when('surnameChangedSinceBirth', isTrueRequire),
	},
	{
		key: 'dateSurnameChanged',
		label: 'Date Surname Changed ' + dateFormatLabel,
		type: 'date',
		fieldProps: {
			maxDate: new Date(),
			show: ({ values }) => values && isTrue(values.surnameChangedSinceBirth)
		},
		required: 'conditional',
		validator: fieldTypes.date.validator.when('surnameChangedSinceBirth', isTrueRequire),
	},
	{
		key: 'drivingLicenceAvailable',
		label: 'Driving Licence available?',
		type: 'yesNo',
	},
	{
		key: 'drivingLicenceNumber',
		label: 'Driving Licence Number',
		type: 'text',
		fieldProps: {
			show: ({ values }) => values && isTrue(values.drivingLicenceAvailable)
		},
		required: 'conditional',
		validator: yup.string().when('drivingLicenceAvailable', isTrueRequire),
	},

	// SIA Licence
	{
		key: 'SIALicenceAvailable',
		label: 'SIA Licence Available?',
		type: 'yesNo',
	},
	{
		key: 'SIALicenceType',
		label: 'SIA Licence Type',
		type: 'text',
		fieldProps: {
			show: ({ values }) => values && isTrue(values.SIALicenceAvailable)
		},
		required: 'conditional',
		validator: yup.string().when('SIALicenceAvailable', isTrueRequire),
	},
	// Food Safety Certificate
	{
		key: 'foodSafetyCertificateAvailable',
		label: 'Food Safety Certificate Available?',
		type: 'yesNo',
	},
	{
		key: 'foodSafetyCertificateLevel',
		label: 'Food Safety Certificate Level',
		type: 'select',
		fieldProps: {
			options: makeOptions(foodSafetyCertificateLevels),
			show: ({ values }) => values && isTrue(values.foodSafetyCertificateAvailable)
		},
		required: 'conditional',
		validator: yup.string().when('foodSafetyCertificateAvailable', isTrueRequire),
	},
	{
		key: 'hasUnspentConvictions',
		label: 'Has unspent criminal convictions?',
		type: 'yesNo',
	},
	{
		key: 'convictionsDescription',
		label: 'Convictions description',
		type: 'textarea',
		fieldProps: {
			placeholder: 'Detail conviction(s) and date(s)',
			show: ({ values }) => values && isTrue(values.hasUnspentConvictions),
		},
		required: 'conditional',
		validator: yup.string().when('hasUnspentConvictions', isTrueRequire),
	},

	// Interview Results
	{
		key: 'interviewResults',
		type: 'object',
		label: 'Interview Results',
		fieldProps: {
			component: 'InterviewResults'
		},
		children: [
			InterviewResultSchema('attitude', 'Attitude'),
			InterviewResultSchema('languageSkills', 'Language Skills'),
			InterviewResultSchema('appearance', 'Appearance'),
			InterviewResultSchema('workExperience', 'Work Experience'),
			InterviewResultSchema('requiredSkills', 'Required Skills'),
			InterviewResultSchema('charisma', 'Charisma'),
			{
				key: 'notes',
				label: 'Notes',
				type: 'textarea',
				required: 'never',
			}
		]
	},
	{
		key: 'pensionOptOutDiscussed',
		label: 'Pension Opt Out Discussed?',
		type: 'yesNo',
	},
	{
		key: 'settlementApplication',
		label: 'Settlement Status Provided?',
		type: 'yesNo',
	},
	{
		key: 'referencesRequested',
		label: 'References Requested?',
		type: 'yesNo',
	},
	{
		key: 'referenceNotes',
		label: 'References Notes',
		type: 'textarea',
	},
	{
		key: 'referAFriendDiscussed',
		label: 'Refer a friend discussed?',
		type: 'yesNo',
	},


	// Address History
	{
		key: 'addressHistory',
		label: 'Address History',
		type: 'fieldArray',
		fieldProps: {
			component: 'AddressHistory',
		},
		children: [
			...AddressSchema().children,
			{
				key: 'from',
				label: 'Date moved in ' + dateFormatLabel,
				type: 'date',
				// TODO: use this config in AddressHistory.jsx
			},
			{
				key: 'to',
				label: 'Date moved out ' + dateFormatLabel,
				type: 'date',
				// TODO: use this config in AddressHistory.jsx
			},
		],
		required: 'conditional',
		validator: (subSchema) => makeValidator(
			yup.array().of(subSchema),
			s => s.test(minimumFiveYearsTest),
		),
		defaultLength: 1,
	},

	// Payment Details
	{
		key: 'paymentDetails',
		type: 'object',
		label: 'Payment Details',
		children: [
			{
				key: 'available',
				label: 'Bank account available?',
				type: 'yesNo',
			},
			{
				key: 'bankName',
				label: 'Bank Name',
				type: 'select',
				fieldProps: {
					options: makeOptions(bankNames),
					show: ({ values }) => values && values.paymentDetails && isTrue(values.paymentDetails.available),
				},
				required: 'conditional',
				validator: yup.string().when('available', isTrueRequire),
			},
			{
				key: 'bankNameCustom',
				label: 'Custom Bank Name',
				type: 'text',
				fieldProps: {
					show: ({ values }) =>
						(values && values.paymentDetails)
						&& isTrue(values.paymentDetails.available)
						&& values.paymentDetails.bankName === "Other"
					,
				},
				required: 'conditional',
				validator: yup.string().when(['available', 'bankName'], {
					is: (available, bankName) => available && bankName === 'Other',
					then: s => s.required(),
				})
			},
			{
				key: 'accountHolderName',
				label: 'Account Holder Name',
				type: 'text',
				fieldProps: {
					show: ({ values }) => values && values.paymentDetails && isTrue(values.paymentDetails.available),
				},
				required: 'conditional',
				validator: yup.string().when('available', isTrueRequire),
			},
			{
				key: 'sortCode',
				label: 'Sort Code',
				type: 'text',
				fieldProps: {
					show: ({ values }) => values && values.paymentDetails && isTrue(values.paymentDetails.available),
				},
				required: 'conditional',
				validator: yup.string().when('available', { is: true, then: s => s.test(sortCodeTest) })
			},
			{
				key: 'accountNumber',
				label: 'Account Number',
				type: 'text',
				fieldProps: {
					show: ({ values }) => values && values.paymentDetails && isTrue(values.paymentDetails.available),
				},
				required: 'conditional',
				validator: yup.string().when('available', { is: true, then: s => s.min(8, 'Account Number must be ${min} digits long').max(8, 'Account Number must be ${max} digits long').required() }),
			},
		]
	},

	// Employment History
	{
		key: 'employmentHistory',
		label: 'Employment History',
		type: 'fieldArray',
		fieldProps: {
			component: 'EmploymentHistory',
		},
		defaultLength: 1,
		children: [
			{
				key: 'companyName',
				label: 'Company Name',
				type: 'text',
			},
			{
				key: 'position',
				label: 'Position Held',
				type: 'text',
			},
			{
				key: 'datesWorked',
				type: 'object',
				label: 'Dates Worked',
				children: [
					{
						key: 'from',
						label: 'Date Worked From ' + dateFormatLabel,
						type: 'date',

					},
					{
						key: 'to',
						label: 'Date Worked Until ' + dateFormatLabel,
						type: 'date',

					},
				]
			},
			{
				key: 'duties',
				label: 'Duties',
				type: 'textarea',
			},
			{
				key: 'reasonsForLeaving',
				label: 'Reason(s) for Leaving',
				type: 'textarea',
			},
			AddressSchema('companyAddress'),
			{
				key: 'referee',
				type: 'object',
				label: 'Referee',
				children: [
					{
						key: 'name',
						label: 'Referee Name',
						type: 'text',
					},
					{
						key: 'position',
						label: 'Referee Position',
						type: 'text',
					},
					{
						key: 'telephone',
						label: 'Referee Telephone',
						type: 'text',
					},
					{
						key: 'email',
						label: 'Referee E-mail',
						type: 'text',
					},
				]
			},
		]
	},

	// Referee Consent
	{
		key: 'refereeConsent',
		label: 'Referee consent provided',
		type: 'checkbox',
		defaultValue: false,
		fieldProps: {
			value: 'Consent provided',
		},
		required: 'conditional',
		validator: agreementValidator('Referee consent is required'),
	},

	// Employment Scheme
	{
		key: 'employmentScheme',
		label: 'Employment Scheme',
		type: 'radioGroup',
		fieldProps: {
			options: makeOptions(employmentSchemes)
		},
		validator: yup.string().oneOf(employmentSchemes),
	},
	{
		key: 'UTRNumber',
		label: 'UTR Number',
		type: 'text',
		fieldProps: {
			show: ({ values }) => values && values.employmentScheme === "Self Employed",
		},
		required: 'conditional',
		validator: yup.string().when('employmentScheme', {
			is: v => v === 'Self Employed',
			then: s => s.required(),
		}),
	},
	{
		key: 'NINumberAvailable',
		label: 'National Insurance Number Available?',
		type: 'yesNo',
	},
	{
		key: 'NINumber',
		label: 'National Insurance Number',
		type: 'text',
		fieldProps: {
			show: ({ values }) => values && isTrue(values.NINumberAvailable)
		},
		required: 'conditional',
		validator: makeValidator(
			yup.string().when('NINumberAvailable', {
				is: true,
				then: s => s.test('len', 'Must be exactly 9 characters', val => val == null || val == '' || val.length === 9)
			}),
			s => s.when('NINumberAvailable', isTrueRequire),
		),
	},

	// Previous Agency
	{
		key: 'previousAgencies',
		label: 'Previous agencies',
		type: 'textarea',
		fieldProps: {
			placeholder: 'Please detail',
		}
	},

	// Working Hours
	{
		key: 'availability',
		label: 'Availability',
		type: 'object',
		fieldProps: {
			component: 'Availability',
		},
		children:
			Object.entries(daysOfTheWeek).map(([key, label]) => ShiftArraySchema(key, label)),
		toCSV: {
			include: 'never',
		}
	},

	// Opt-out of 48 hour working week agreement
	{
		key: 'optOut48HourWorkingWeek',
		type: 'yesNo',
		label: 'Please confirm your agreement to work in excess of an average of 48 hours per week by signing the declaration below.',
	},

	{
		key: 'agreementTo48HourWorkingWeek',
		type: 'text',
		label: `You have indicated your agreement to work in excess of an average of 48 hours per week. Please sign the declaration below. The Company is required to retain this form to allow for inspection by the enforcing authority. \n
							DECLARATION \n I agree to work, when necessary, in excess of 48 hours per week. I understand that I may terminate this agreement at any time subject to giving 3 months\' notice in writing. \n PLEASE SIGN YOUR NAME BELOW`,
		fieldProps: {
			show: ({ values }) => values && isTrue(values.optOut48HourWorkingWeek),
		},
		required: 'conditional',
		validator: yup.string().when('optOut48HourWorkingWeek', isTrueRequire),

	},

	// Health & Disability
	{
		key: 'hasHealthIssues',
		label: 'Has relevant health issues',
		type: 'yesNo',
	},
	{
		key: 'healthIssuesDescription',
		label: 'Health Issues Description',
		type: 'textarea',
		fieldProps: {
			show: ({ values }) => values && isTrue(values.hasHealthIssues),
		},
		required: 'conditional',
		validator: yup.string().when('hasHealthIssues', isTrueRequire),
	},
	{
		key: 'assistanceRequired',
		label: 'Assistance required',
		type: 'textarea',
		fieldProps: {
			show: ({ values }) => values && isTrue(values.hasHealthIssues),
		},
		required: 'conditional',
		validator: yup.string().when('hasHealthIssues', isTrueRequire),
	},

	// Consent
	{
		key: 'equalOpportunitiesAgreement',
		label: 'Equal Opportunities Agreement agreed',
		type: 'checkbox',
		fieldProps: {
			value: 'Equal Opportunities Agreement agreed',
		},
		validator: agreementValidator(),
	},
	{
		key: 'dataProtectionAgreementPart1',
		label: 'Data Protection Agreement part 1 agreed',
		type: 'checkbox',
		fieldProps: {
			value: 'Data Protection Agreement part 1 agreed',
		},
		validator: agreementValidator(),
	},
	{
		key: 'dataProtectionAgreementPart2',
		label: 'Data Protection Agreement part 2 agreed',
		type: 'checkbox',
		fieldProps: {
			value: 'Data Protection Agreement part 2 agreed',
		},
		validator: agreementValidator(),
	},
	{
		key: 'candidateSigned',
		label: 'Candidate Signed as',
		type: 'text'
	},

	// Documents
	getFieldPreset('documents'),

	// Account
	{
		key: 'heardAboutHumanOne',
		label: 'How did you hear about Human One?',
		type: 'select',
		fieldProps: {
			options: makeOptions(heardAboutSources),
		}
	},


]);

// TODO: remove window instances after development
window.CandidateModel = CandidateModel;

export default CandidateModel;



// WEBPACK FOOTER //
// ./src/modules/user/models/candidate.model.js